/**
 * PATH: packages/ui/src/components/settings/email-label-prefs/labelNesting.ts
 * FILE_ID: UI-SETTINGS-LABELPREFS-NESTING-001
 * PURPOSE: Pure ordering + parent-option helpers for the folders-style
 *          one-level label nesting in the settings section.
 * WHY: Operator 2026-07-06 — "like in folders in emails allow me to add
 *      main category like Private and such on it". The section renders
 *      each parent followed by its children; the edit form only offers
 *      valid nesting targets (same tier, top-level, not-self, and only
 *      for labels that have no children — one level deep by contract).
 * FLOW:
 *   orderLabelsNested(rows)        -> [{category, nested}] render order
 *   nestingTargetsFor(rows, row)   -> [{id, label}] for the "Nest under" select
 * RELATED:
 *   - PersonalCategoryRow.tsx: consumes parentOptions + nested
 *   - LabelPreferencesSection.tsx: the only mount
 */

import type { LabelCategory } from './types'

export interface NestedLabelRow {
  category: LabelCategory
  /** True when rendered indented under the preceding top-level row. */
  nested: boolean
}

/**
 * f(rows) → render order: each top-level label immediately followed by its
 * children. Orphans (parent id not in the set) render at top level — a
 * label is never hidden by a stale parent reference.
 */
export function orderLabelsNested(rows: LabelCategory[]): NestedLabelRow[] {
  const ids = new Set(rows.map((r) => r.id).filter((v) => v != null))
  const byParent = new Map<number, LabelCategory[]>()
  const tops: LabelCategory[] = []
  for (const row of rows) {
    if (row.parent_id != null && ids.has(row.parent_id)) {
      const kids = byParent.get(row.parent_id) ?? []
      kids.push(row)
      byParent.set(row.parent_id, kids)
    } else {
      tops.push(row)
    }
  }
  const out: NestedLabelRow[] = []
  for (const top of tops) {
    out.push({ category: top, nested: false })
    if (top.id != null) {
      for (const kid of byParent.get(top.id) ?? []) {
        out.push({ category: kid, nested: true })
      }
    }
  }
  return out
}

/**
 * f(rows, row) → valid "Nest under" targets for one row: same-tier labels
 * that are top-level and not the row itself. Returns [] when the row has
 * children of its own (nesting it would push them 3 deep — the backend
 * rejects that, so the UI doesn't offer it).
 */
export function nestingTargetsFor(
  rows: LabelCategory[],
  row: LabelCategory,
): Array<{ id: number; label: string }> {
  if (row.id != null && rows.some((r) => r.parent_id === row.id)) return []
  return rows
    .filter(
      (r): r is LabelCategory & { id: number } =>
        r.id != null && r.id !== row.id && r.parent_id == null,
    )
    .map((r) => ({ id: r.id, label: r.label }))
}
